import type { TokenUsdPriceData } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import type { BaseTokenInfo, TokenInfo } from "../types/base"
import { isBaseToken, isUnifiedToken } from "./token"

export function getTokenPrice(
  token: TokenInfo | null,
  priceData?: TokenUsdPriceData
): number | null {
  if (!priceData || !token) return null

  if (isBaseToken(token) && priceData[token.defuseAssetId]) {
    return priceData[token.defuseAssetId].price
  }

  if (isUnifiedToken(token)) {
    for (const grouped of token.groupedTokens) {
      if (isBaseToken(grouped) && priceData[grouped.defuseAssetId]) {
        return priceData[grouped.defuseAssetId].price
      }
    }
  }

  return null
}

/**
 * Inverse of {@link getTokenUsdPrice}: how much of `token` is worth `usdValue`,
 * formatted to the token's decimals so it can be parsed with `parseUnits`.
 * Returns null when no price is available or the result rounds to zero.
 */
export function usdAmountToTokenAmountString(
  usdValue: number,
  token: BaseTokenInfo | TokenInfo | null,
  priceData?: TokenUsdPriceData
): string | null {
  const price = getTokenPrice(token, priceData)
  if (token == null || price == null || price <= 0) return null

  const decimals = isBaseToken(token)
    ? token.decimals
    : token.groupedTokens.find(isBaseToken)?.decimals
  if (decimals == null) return null

  const tokenAmount = (usdValue / price).toFixed(decimals)
  // Guard against rounding to 0 for high-priced tokens with few decimals.
  return Number(tokenAmount) > 0 ? tokenAmount : null
}

const getTokenUsdPrice = (
  tokenAmount: string,
  token: TokenInfo | null,
  tokensUsdPriceData?: TokenUsdPriceData
): number | null => {
  try {
    if (!tokensUsdPriceData || !token || !tokenAmount) return null
    const numberTokenAmount = +tokenAmount
    if (Number.isNaN(numberTokenAmount) || !Number.isFinite(numberTokenAmount))
      return null
    let tokenUsdPriceData = null
    if (isBaseToken(token) && tokensUsdPriceData[token.defuseAssetId]) {
      tokenUsdPriceData = tokensUsdPriceData[token.defuseAssetId]
    } else if (isUnifiedToken(token)) {
      for (const groupedToken of token.groupedTokens) {
        if (
          isBaseToken(groupedToken) &&
          tokensUsdPriceData[groupedToken.defuseAssetId]
        ) {
          tokenUsdPriceData = tokensUsdPriceData[groupedToken.defuseAssetId]
          break
        }
      }
    }
    if (!tokenUsdPriceData) return null
    return numberTokenAmount * tokenUsdPriceData.price
  } catch {
    return null
  }
}
export default getTokenUsdPrice
